// Pages en anglais (expatriés, anglais britannique). translationOf : page
// française équivalente, pour les balises hreflang et les liens de langue.
import { AREAS } from './site';
import leakPhoto from '../assets/dde.webp';
import gazImg from '../assets/gaz_traceur_detecteur.webp';
import thermoImg from '../assets/thermographie_chauffage_sol.webp';
import inspectionImg from '../assets/inspection_camera_egout.webp';
import fluoresceineImg from '../assets/fluoresceine_uv.webp';
import humidimetreImg from '../assets/humidimetre_mur.webp';

const LEAK_TYPES = [
  '[Concealed and buried water pipes](/en/water-pipe-leak): in a floor slab, a wall, under the garden.',
  '[Heating and underfloor heating](/en/heating-leak): boiler losing pressure, radiators, heating loops in the screed.',
  '[Drains and sewers](/en/drain-leak): downpipes, connections, buried pipes, sewer smells.',
  '[Showers, baths and leaks under tiles](/en/shower-leak): supply, drain or waterproofing.',
  '[Water ingress](/en/water-ingress): roof, terrace, façade, basement.',
  '[High water consumption](/en/high-water-usage): meter turning, abnormal bill, leak with no visible sign.',
];

const PARENT = '/en/leak-detection-luxembourg';

export const pagesEn = [
  {
    path: '/en/leak-detection-luxembourg',
    lang: 'en',
    translationOf: '/recherche-fuite-luxembourg',
    nav: 'Leak detection in Luxembourg',
    summary: 'Non-destructive detection of water leaks and water ingress across the Grand Duchy, report within 72 hours.',
    title: 'Leak Detection Luxembourg | Non-Destructive | Expomis',
    description: 'Non-destructive water leak detection across the Grand Duchy of Luxembourg: pipes, heating, drains, water ingress. Report for your insurer within 72 hours.',
    eyebrow: 'Grand Duchy of Luxembourg',
    h1: 'Leak detection in Luxembourg',
    serviceType: 'Leak detection',
    image: leakPhoto,
    imageAlt: 'Water leaking from a copper pipe',
    areaServed: AREAS.lu,
    intro: [
      'Expomis finds water leaks without demolition anywhere in the Grand Duchy of Luxembourg, from our office in Steinfort. Concealed pipe, underfloor heating, drain, shower, water coming in through the roof or the façade: we find the source of the leak, pinpoint its location and give you a report within 72 hours that you can send to your insurer.',
      'We are also loss adjusters. Beyond the leak itself, we know what your insurer needs to process a [water damage](/en/water-damage) claim.',
    ],
    sections: [
      {
        h2: 'When do you need leak detection?',
        body: ['A hidden leak is rarely seen directly. What you see are its effects:'],
        list: [
          'a damp patch spreading on the ceiling or a wall;',
          'a water meter that keeps turning with everything switched off, or an unusually high water bill;',
          'a boiler that loses pressure and needs topping up regularly;',
          'blistering paint, darkening grout, a musty smell;',
          'tiles coming loose, wooden flooring swelling;',
          'water appearing in the flat below.',
        ],
        after: [
          'The longer a leak lasts, the more damage it does: soaked materials, mould, sometimes structural damage. Finding it quickly also keeps repair costs down.',
        ],
      },
      {
        h2: 'Non-destructive leak detection: what it means',
        body: [
          'Non-destructive does not mean nothing will ever be opened: the pipe still has to be repaired. It means the leak is located without opening things up at random. Instead of breaking open a whole wall or redoing an entire bathroom, only the spot we have marked is opened, over a small area.',
        ],
      },
      {
        h2: 'The leaks we find',
        list: LEAK_TYPES,
      },
      {
        h2: 'Our detection methods',
        body: [
          'No single method works in every case. The choice depends on the system (cold water, hot water, heating, drainage), its pressure and the materials covering it. We often combine several methods to cross-check the results.',
        ],
        list: [
          '[moisture mapping](/en/leak-detection-methods/moisture-mapping) to define the affected area;',
          '[pressure testing](/en/leak-detection-methods/pressure-test) to confirm the leak and identify the circuit involved;',
          '[thermal imaging](/en/leak-detection-methods/thermal-imaging) for hot water and underfloor heating;',
          '[acoustic detection](/en/leak-detection-methods/acoustic-detection) for pressurised pipes;',
          '[tracer gas](/en/leak-detection-methods/tracer-gas) for small, buried or under-slab leaks;',
          '[fluorescein dye](/en/leak-detection-methods/fluorescein), [smoke testing](/en/leak-detection-methods/smoke-test) and [CCTV drain inspection](/en/leak-detection-methods/cctv-drain-survey) for drainage;',
          '[endoscopy](/en/leak-detection-methods/endoscopy) to look into inaccessible spaces.',
        ],
        after: ['How each method works and where its limits lie: [our leak detection methods](/en/leak-detection-methods).'],
      },
      {
        h2: 'How a visit works',
        ordered: true,
        list: [
          'You describe the symptoms to us by phone or email, with no obligation.',
          'On site, we observe, photograph and measure moisture to define the affected area.',
          'We pressure-test the circuits to find out which one is leaking, then pinpoint the leak with the appropriate method.',
          'The location of the leak is marked on site.',
          'Within 72 hours you receive a full report with photos and measurements.',
        ],
      },
      {
        h2: 'Leak detection and insurance',
        body: [
          'In Luxembourg, many home insurance policies cover the cost of leak detection when the leak has caused water damage. Terms vary from one policy to another: check yours or ask your insurance agent. Who pays for what also depends on where the leak is: see our guide [Water leak in Luxembourg: who pays?](/en/water-leak-who-pays).',
          "Our report describes the findings, the methods used, the source identified and the damage observed: it is the document insurers expect in order to process the claim. And if you feel the assessment by your insurer's adjuster does not reflect reality, we can act as your independent adjuster.",
        ],
      },
      {
        h2: 'Where do we work?',
        body: [
          'Throughout the Grand Duchy: [Luxembourg City](/en/leak-detection-luxembourg-city), Esch-sur-Alzette, Differdange, Dudelange, Pétange, Sanem, Mamer, Strassen, Bertrange, Hesperange, Capellen, Mersch, Ettelbruck, Diekirch, Wiltz, Grevenmacher, Remich, Echternach and every other commune.',
          'Based in Steinfort, on the Belgian border, we also regularly work in the south of the Belgian province of Luxembourg.',
        ],
      },
    ],
    faq: [
      {
        q: 'How much does leak detection cost in Luxembourg?',
        a: 'Each leak search is charged per job, at a price agreed with you before we start; if the leak is found in a single visit without taking anything apart, a flat rate applies when it is cheaper. The price depends on the type of leak, how much of the system needs testing and the methods required. In many cases, home insurance covers the cost. See [leak detection cost](/en/leak-detection-cost).',
      },
      {
        q: 'Do you have to break things open to find a leak?',
        a: 'No. The leak is located without demolition. An opening is still needed for the repair, but only at the exact spot of the leak.',
      },
      {
        q: 'What should I do until you arrive?',
        a: 'If the leak is significant, turn off the main water valve. Switch off the electricity in affected rooms if water is near electrical installations. Photograph the damage and notify your insurer. Avoid dismantling or altering the installation: it makes the leak harder to locate.',
      },
      {
        q: 'When will I receive the report?',
        a: 'Within 72 hours. It includes photos and measurements and can be sent to your insurer as it is.',
      },
      {
        q: 'Do you handle emergencies?',
        a: 'Yes, an emergency service is available 24/7 on +352 661 181 082.',
      },
    ],
    related: ['/en/water-damage', '/en/water-leak-who-pays', '/en/water-pipe-leak'],
  },
  {
    path: '/en/leak-detection-luxembourg-city',
    lang: 'en',
    translationOf: '/recherche-fuite-luxembourg-ville',
    parent: PARENT,
    nav: 'Leak detection in Luxembourg City',
    summary: 'Flats in co-ownership buildings, town houses and old buildings, from the Ville Haute to Kirchberg.',
    title: 'Leak Detection Luxembourg City | No Demolition | Expomis',
    description: 'Non-destructive leak detection in Luxembourg City: flats in co-ownership buildings, leak from the flat above, car parks, old buildings. Report within 72 hours.',
    eyebrow: 'Luxembourg City',
    h1: 'Leak detection in Luxembourg City',
    serviceType: 'Leak detection',
    areaServed: [{ '@type': 'City', name: 'Luxembourg' }],
    intro: [
      'Expomis finds water leaks and water ingress without demolition in every district of Luxembourg City: Ville Haute, Gare, Bonnevoie, Limpertsberg, Belair, Merl, Hollerich, Gasperich, Cessange, Kirchberg, Neudorf, Cents, Clausen, Grund, Pfaffenthal and the rest.',
      'You receive a report within 72 hours that identifies the source of the leak: the document your insurer, the syndic or your landlord will ask for.',
    ],
    sections: [
      {
        h2: 'In a flat: which home is the leak coming from?',
        body: [
          'Much of the housing in Luxembourg City is flats in co-ownership buildings (copropriétés). The leak often shows up on the ceiling of the flat below, and the first question is not a technical one: who is involved? The neighbour upstairs, the shared riser, the roof terrace?',
          'We isolate each circuit in the suspected flat and pressure-test it, which establishes whether or not it is leaking. We then pinpoint the leak. The report states whether the source is in a private part or a common part of the building: the syndic and the insurers need this to decide who covers the claim.',
        ],
      },
      {
        h2: 'Newer residences: underfloor heating, service shafts, car parks',
        body: [
          'In the residences built over the last few decades, in Kirchberg, Gasperich or Cloche d’Or as elsewhere in the city, the same situations keep coming up:',
        ],
        list: [
          '[underfloor heating](/en/heating-leak) losing pressure, with heating loops embedded in the screed: we test each loop at the manifold, then the [thermal imaging camera](/en/leak-detection-methods/thermal-imaging) reveals the leak;',
          'pipes in service shafts and suspended ceilings, which [endoscopy](/en/leak-detection-methods/endoscopy) lets us inspect through a small opening;',
          'water ingress in underground car parks and basements, through buried walls, slab joints or the roof terrace above: [fluorescein](/en/leak-detection-methods/fluorescein) links the water that appears to the point where it gets in.',
        ],
      },
      {
        h2: 'Old buildings: protecting what cannot be replaced',
        body: [
          'In the Ville Haute, Limpertsberg, Belair or the town houses of Gare and Bonnevoie, plumbing has often been altered over successive renovations. Old sections of galvanised steel, sometimes lead, sit alongside copper or multilayer pipe, and the joints between these materials are common leak points.',
          'These buildings also have parquet floors, mouldings and old tiles that cannot be matched. This is where non-destructive detection matters most: opening up in the right place, over a small area, rather than probing a whole wall or floor.',
        ],
      },
      {
        h2: 'In the valleys: a leak or damp from the ground?',
        body: [
          'In Grund, Clausen and Pfaffenthal, houses stand along the Alzette, often built against the rock. Cellar walls there are damp for reasons that have nothing to do with a leak: groundwater, rising damp. Before any work starts, we establish where the water is coming from: [moisture mapping](/en/leak-detection-methods/moisture-mapping), then pressure tests on the pipes. If they are watertight, we tell you, and the report records it.',
        ],
      },
      {
        h2: 'What we find in Luxembourg City',
        list: LEAK_TYPES,
      },
    ],
    faq: [
      {
        q: 'I rent my flat and have a leak: who should call you?',
        a: 'First notify your landlord or the letting agency, and your insurer. The leak detection can be ordered by the tenant, the landlord or the syndic: the report, which identifies the source of the leak, is then used to decide who pays for the repair. The exact rules depend on your lease and the insurance policies. See our guide [water leak in Luxembourg: who pays?](/en/water-leak-who-pays)',
      },
      {
        q: "Water is coming from the flat above, but the neighbour can't be reached. What should I do?",
        a: 'Notify the syndic: they have access to the common parts and can contact the co-owner. To pinpoint the leak, we will need access to the flat it is coming from. In the meantime, photograph the damage and report the claim to your insurer.',
      },
      {
        q: 'Do you have to break things open to find the leak?',
        a: 'No. The leak is located without demolition. An opening is still needed for the repair, but only at the spot we have marked.',
      },
      {
        q: 'When will I receive the report?',
        a: 'Within 72 hours. It includes photos and measurements and can be sent as it is to the insurer or the syndic.',
      },
      {
        q: 'Do you handle emergencies?',
        a: 'Yes, an emergency service is available 24/7 on +352 661 181 082.',
      },
    ],
    related: ['/en/leak-detection-luxembourg', '/en/water-leak-who-pays', '/en/heating-leak'],
  },
  {
    path: '/en/water-pipe-leak',
    lang: 'en',
    translationOf: '/recherche-fuite-canalisation',
    parent: PARENT,
    nav: 'Water pipe leak',
    summary: 'Water pipe concealed in a floor slab or a wall, or buried under the garden.',
    title: 'Concealed or Buried Water Pipe Leak Detection | Expomis',
    description: 'Non-destructive leak detection on concealed and buried water pipes in Luxembourg: pressure testing, thermal imaging, acoustic detection, tracer gas.',
    eyebrow: 'Types of leak',
    h1: 'Leak detection on concealed and buried water pipes',
    serviceType: 'Leak detection',
    image: gazImg,
    imageAlt: 'Leak detection under tiles with tracer gas, detector and ground probe',
    intro: [
      "A water pipe leaking inside a floor slab, a wall or under the garden can't be seen: you only notice the effects, a turning meter, a damp patch, falling pressure. Expomis locates this kind of leak without opening up at random, using the right method for the pipe concerned.",
    ],
    sections: [
      {
        h2: 'Signs of a water pipe leak',
        list: [
          'the water meter keeps turning with all taps and appliances off;',
          'a stain appears on the ceiling, at the foot of a wall or on a screed floor;',
          'one area of the floor is warmer than the rest (hot water leak);',
          'you can hear constant running or hissing;',
          'water pressure at the tap has dropped for no obvious reason.',
        ],
      },
      {
        h2: 'First find the circuit, then the leak',
        body: [
          'A house has several circuits: cold water, hot water, sometimes a recirculation loop, and the heating. Before looking for where the leak is, we establish which circuit it is on by [pressure testing](/en/leak-detection-methods/pressure-test) section by section. This narrows down the search area and avoids false leads.',
        ],
      },
      {
        h2: 'How we pinpoint the leak',
        list: [
          'Hot water pipe: [thermal imaging](/en/leak-detection-methods/thermal-imaging) shows the route of the pipe and the area where hot water is spreading.',
          'Pressurised pipe, including cold water: [acoustic detection](/en/leak-detection-methods/acoustic-detection) picks up the sound of escaping water.',
          'Small, silent leak under a thick slab or underground: [tracer gas](/en/leak-detection-methods/tracer-gas) rises to the surface directly above the leak.',
        ],
        after: ['We often cross-check with two methods to confirm the location before marking it.'],
      },
      {
        h2: 'Buried pipes',
        body: [
          'The pipe supplying the building, a garage, a garden shed or an outdoor tap can run for dozens of metres under the lawn or the driveway. Tracer gas and acoustic detection show where to dig, instead of trenching along its whole length.',
          'Depending on where the leak is, the repair may be the responsibility of the water supplier or the commune rather than the owner. Pinpointing it also tells you who is responsible.',
        ],
      },
    ],
    faq: [
      {
        q: 'My meter keeps turning with everything off: is it always a leak?',
        a: 'Almost always, unless an appliance is using water without you knowing: a toilet cistern that keeps running, a water softener regenerating, a dripping safety valve on the water heater. Check these first; our page on [high water consumption](/en/high-water-usage) explains how.',
      },
      {
        q: 'Does the water need to be turned off during the visit?',
        a: 'For some tests, yes: pressure testing and tracer gas require isolating, or even draining, the section being tested. The interruption only lasts as long as the test.',
      },
    ],
    related: ['/en/high-water-usage', '/en/heating-leak', '/en/leak-detection-luxembourg'],
  },
  {
    path: '/en/heating-leak',
    lang: 'en',
    translationOf: '/recherche-fuite-chauffage',
    parent: PARENT,
    nav: 'Heating and underfloor heating leak',
    summary: 'Boiler losing pressure, underfloor heating, radiators and heating pipes.',
    title: 'Underfloor Heating and Radiator Leak Detection | Expomis',
    description: 'Boiler losing pressure? Leak detection on underfloor heating and radiators without breaking the screed: loop-by-loop testing, thermal imaging, tracer gas.',
    eyebrow: 'Types of leak',
    h1: 'Leak detection on heating and underfloor heating',
    serviceType: 'Leak detection',
    image: thermoImg,
    imageAlt: 'Underfloor heating leak visible on a thermal camera',
    intro: [
      "A boiler that loses pressure and needs regular topping up is the most common sign of a leak in the heating circuit. With underfloor heating, the pipes are embedded in the screed: the leak can't be seen, and opening the floor at random is expensive. Expomis pinpoints the leak without demolition.",
    ],
    sections: [
      {
        h2: 'Signs of a heating leak',
        list: [
          'boiler pressure drops and you have to top it up regularly;',
          'one area of the floor is warmer than the rest, or a stain appears on the ceiling below;',
          'damp at the foot of a wall or under a radiator;',
          'floor covering coming loose or wooden flooring swelling.',
        ],
      },
      {
        h2: 'Before looking for a leak, check there is one',
        body: [
          'Pressure loss does not always come from a leak in the floor. A deflated expansion vessel or a discharging safety valve can cause the same symptom. We check these components, then pressure-test the system loop by loop at the manifold to identify the one that is leaking.',
        ],
      },
      {
        h2: 'Why thermal imaging works well',
        body: [
          'Heating is a hot circuit. When it is heated up, the [thermal camera](/en/leak-detection-methods/thermal-imaging) reveals the route of the pipes under the floor covering, then the hot spot that forms where water is escaping. If the thermal contrast is too weak (thick floor covering, small leak), we use [tracer gas](/en/leak-detection-methods/tracer-gas).',
        ],
      },
    ],
    faq: [
      {
        q: 'My boiler is losing pressure: is it always a leak?',
        a: 'Not necessarily. A deflated expansion vessel or a faulty safety valve produce the same symptom. If these are in order and the pressure keeps dropping, there is very probably a leak in the circuit.',
      },
      {
        q: 'Can underfloor heating be repaired without replacing the whole floor?',
        a: 'Yes, in most cases. Once the leak is located, the screed is opened locally, the pipe is repaired with a coupling, and the floor is closed again. That is the whole point of precise detection.',
      },
    ],
    related: ['/en/water-pipe-leak', '/en/high-water-usage', '/en/leak-detection-luxembourg'],
  },
  {
    path: '/en/drain-leak',
    lang: 'en',
    translationOf: '/recherche-fuite-evacuation',
    parent: PARENT,
    nav: 'Drain and sewer leak',
    summary: 'Drains, downpipes, connections and sewers: leaks, damp and sewer smells.',
    title: 'Drain and Sewer Leak Detection | Smoke Test, CCTV | Expomis',
    description: 'Leak detection on drains, downpipes and sewers: fluorescein dye, smoke testing, CCTV inspection. Sewer smells, stains after using an appliance.',
    eyebrow: 'Types of leak',
    h1: 'Leak detection on drains and sewers',
    serviceType: 'Leak detection',
    image: inspectionImg,
    imageAlt: 'CCTV inspection of a cracked sewer pipe from a manhole',
    intro: [
      'Drains are not under pressure: a leak only shows when water is flowing, after a shower, a wash or a flush. That is why they escape the methods used for pressurised pipes. Expomis uses methods designed for drainage systems: fluorescein dye, smoke testing and CCTV inspection.',
    ],
    sections: [
      {
        h2: 'Signs of a drain leak',
        list: [
          'a stain that appears or grows after a particular appliance is used;',
          'sewer smells in a room or in the basement;',
          'damp at the foot of a downpipe or along a basement wall;',
          'slow drainage or recurring backflow;',
          'subsidence above a buried pipe.',
        ],
      },
      {
        h2: 'Three complementary methods',
        list: [
          '[Fluorescein dye](/en/leak-detection-methods/fluorescein): the water from one appliance at a time is coloured, and we watch where it reappears. This tells us which appliance, which drain is at fault.',
          '[Smoke testing](/en/leak-detection-methods/smoke-test): a non-toxic smoke sent into the system escapes through poorly fitted joints and cracks. Ideal for smells.',
          '[CCTV inspection](/en/leak-detection-methods/cctv-drain-survey): we see inside the pipe, cracks, displaced joints, roots, deformation, back-falls.',
        ],
      },
      {
        h2: 'Buried drains and sewer connections',
        body: [
          'Under a basement slab or in the garden, a cracked drain lets water soak into the ground and can, over time, destabilise it. CCTV inspection locates the defect by the length of cable run from the manhole, so digging happens only in the right place.',
        ],
      },
    ],
    faq: [
      {
        q: 'Do sewer smells always come from a leak?',
        a: 'No. A dried-out trap (on a rarely used appliance) or missing ventilation can let smells back up. A smoke test shows exactly where they get in.',
      },
      {
        q: 'Is fluorescein dangerous?',
        a: 'No. It is a non-toxic dye, used in very low concentration, which is also used for tracing groundwater.',
      },
    ],
    related: ['/en/shower-leak', '/en/water-ingress', '/en/leak-detection-luxembourg'],
  },
  {
    path: '/en/shower-leak',
    lang: 'en',
    translationOf: '/recherche-fuite-douche',
    parent: PARENT,
    nav: 'Shower leak or leak under tiles',
    summary: 'Shower, walk-in shower, bath: supply, drain or waterproofing?',
    title: 'Leaking Shower, Bath or Leak Under Tiles | Expomis',
    description: 'Leaking shower, walk-in shower or bath: we test supply, drain and waterproofing separately to find the real cause, without demolition.',
    eyebrow: 'Types of leak',
    h1: 'Leaking shower, bath or leak under tiles',
    serviceType: 'Leak detection',
    image: fluoresceineImg,
    imageAlt: 'Fluorescein test under UV light: the dyed water reveals a drain leak',
    intro: [
      'A stain on the ceiling below the bathroom, grout turning black, a tile that sounds hollow: the shower is one of the most common sources of leaks. The difficulty is knowing where the water comes from, because there are three possible sources, each needing a different repair.',
    ],
    sections: [
      {
        h2: 'Three possible sources',
        list: [
          'The supply: the hot or cold water pipe to the mixer, concealed in the wall. It leaks all the time, even when the shower is not in use.',
          'The drain: waste fitting, trap, connection to the drain pipe. It only leaks when water is draining.',
          'The waterproofing: grout, shower tray, membrane under the tiles. Water gets through when it runs down the walls or sits on the floor.',
        ],
        after: [
          "Regrouting won't help if the leak comes from the supply. Replacing the trap won't help if it's the waterproofing. That's why each source should be tested separately before any work starts.",
        ],
      },
      {
        h2: 'How we tell them apart',
        list: [
          'Supply: [pressure testing](/en/leak-detection-methods/pressure-test) of the pipes, and [thermal imaging](/en/leak-detection-methods/thermal-imaging) to follow the hot water route.',
          'Drain: water dyed with [fluorescein](/en/leak-detection-methods/fluorescein) is poured only into the drain, without wetting the walls, and we watch whether it reappears in the stain.',
          'Waterproofing: we wet one zone at a time (walls, corners, base of the walls, threshold) and track changes in moisture with a [moisture meter](/en/leak-detection-methods/moisture-mapping).',
        ],
      },
      {
        h2: 'Walk-in showers',
        body: [
          'Without a shower tray, a walk-in shower relies entirely on the membrane under the tiles. Defects are most often found around the floor drain, at the base of the walls and at the threshold. These are the areas we test first.',
        ],
      },
      {
        h2: 'In a flat',
        body: [
          'The leak usually shows in the flat below. Our tests establish whether it really comes from your bathroom, and from which element, which is essential for the [water damage](/en/water-damage) claim.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is regrouting enough to fix a leaking shower?',
        a: "Only if water is getting through the grout. If the leak comes from the supply, the drain or the membrane under the tiles, regrouting won't change anything.",
      },
    ],
    related: ['/en/drain-leak', '/en/water-damage', '/en/leak-detection-luxembourg'],
  },
  {
    path: '/en/water-ingress',
    lang: 'en',
    translationOf: '/recherche-infiltration',
    parent: PARENT,
    nav: 'Water ingress',
    summary: 'Roof, terrace, façade, basement: finding where rainwater gets in.',
    title: 'Water Ingress Detection: Roof, Terrace, Façade | Expomis',
    description: 'Finding water ingress through a roof, terrace, façade or basement: targeted water testing, fluorescein dye, thermal imaging, moisture mapping.',
    eyebrow: 'Types of leak',
    h1: 'Water ingress detection: roof, terrace, façade, basement',
    serviceType: 'Water ingress detection',
    image: humidimetreImg,
    imageAlt: 'Measuring the moisture of a wall affected by water ingress',
    intro: [
      "Water ingress is not a pipe leak: the water comes from outside, usually rain, through a defect in the building envelope. It often appears far from where it gets in, and only in certain weather, which makes it hard to locate. Expomis traces the water's path back to its entry point.",
    ],
    sections: [
      {
        h2: 'Signs of water ingress',
        list: [
          'a stain on the ceiling under a roof or a terrace that grows after rain;',
          'damp on a wall exposed to driving rain, or around a window;',
          'a basement that takes on water after a storm;',
          'marks that change with the weather rather than with water use in the house.',
        ],
      },
      {
        h2: 'Water ingress or something else?',
        body: [
          'A damp patch is not necessarily water ingress. A pipe leak, condensation or rising damp produce similar symptoms. [Moisture mapping](/en/leak-detection-methods/moisture-mapping), the link with the weather and [pressure testing](/en/leak-detection-methods/pressure-test) of nearby pipes settle the question.',
        ],
      },
      {
        h2: 'How we locate water ingress',
        list: [
          'Targeted water testing: we wet one precise area (a flashing, a terrace outlet, a threshold, a window frame) and watch whether water comes through inside. Dyed with [fluorescein](/en/leak-detection-methods/fluorescein), it is unmistakable.',
          '[Thermal imaging](/en/leak-detection-methods/thermal-imaging): damp areas of a ceiling or insulation show up colder, revealing the extent and path of the water.',
          '[Endoscopy](/en/leak-detection-methods/endoscopy): to look into a suspended ceiling, a lining or a cavity without dismantling.',
        ],
      },
      {
        h2: 'Flat roofs and terraces',
        body: [
          'On a flat roof or a terrace, water rarely gets in through the middle of the surface. Weak points are at the upstands against the walls, around outlets, at joints and at the thresholds of French windows. We test these areas one by one to isolate the one letting water through.',
        ],
      },
    ],
    faq: [
      {
        q: 'Do we need to wait for rain to find water ingress?',
        a: 'No. We recreate the water flow with targeted water testing, one zone at a time, which lets us find the entry point in dry weather.',
      },
    ],
    related: ['/en/drain-leak', '/en/water-damage', '/en/leak-detection-luxembourg'],
  },
  {
    path: '/en/high-water-usage',
    lang: 'en',
    translationOf: '/surconsommation-eau',
    parent: PARENT,
    nav: 'High water consumption',
    summary: 'Abnormal bill, meter turning: finding the leak that leaves no trace.',
    title: 'High Water Bill, Meter Running | Find the Leak | Expomis',
    description: "Abnormal water bill or meter running with all taps off? Checks you can do yourself, then non-destructive detection of the hidden leak.",
    eyebrow: 'Types of leak',
    h1: 'High water consumption: finding the hidden leak',
    serviceType: 'Leak detection',
    intro: [
      'A water bill that rises sharply with no change in your habits, a meter that turns with everything off: water is escaping somewhere, sometimes without any visible sign, because it goes straight into a drain or into the ground.',
    ],
    sections: [
      {
        h2: 'Check for yourself in 30 minutes',
        ordered: true,
        list: [
          'Turn off all taps and stop appliances that use water (washing machine, dishwasher, water softener, irrigation).',
          'Note the meter reading, or watch the small star wheel that turns whenever water flows.',
          'Wait 30 minutes without using any water, then read the meter again.',
          'If the reading has changed, there is a leak after the meter.',
        ],
        after: [
          'Before calling us, check the most common invisible leaks: a toilet cistern that keeps running into the bowl (a few drops of food colouring in the cistern will show it), a water heater safety valve dripping into the drain, a water softener stuck in regeneration, an outdoor tap.',
        ],
      },
      {
        h2: "If these checks don't find anything",
        body: [
          'The leak is then probably on a [concealed or buried pipe](/en/water-pipe-leak). We isolate the circuits one by one and [pressure-test](/en/leak-detection-methods/pressure-test) them to find out which one is losing water, then pinpoint the leak using [acoustic detection](/en/leak-detection-methods/acoustic-detection) or [tracer gas](/en/leak-detection-methods/tracer-gas).',
        ],
      },
      {
        h2: 'Your bill',
        body: [
          'Some suppliers or communes grant a reduction on the bill when a hidden leak has been located and repaired. Ask yours: our report documents the leak and its location.',
        ],
      },
    ],
    faq: [
      {
        q: 'Will my insurance pay for the lost water?',
        a: 'Rarely. Policies generally cover damage caused by water, and often the cost of leak detection, but less often the lost consumption. Check your terms.',
      },
    ],
    related: ['/en/water-pipe-leak', '/en/heating-leak', '/en/leak-detection-luxembourg'],
  },
  {
    path: '/en/water-damage',
    lang: 'en',
    translationOf: '/degat-des-eaux',
    nav: 'Water damage',
    summary: 'Finding where the water comes from and documenting the claim for your insurer.',
    serviceType: 'Water damage assessment',
    areaServed: AREAS.lu,
    title: 'Water Damage in Luxembourg: Cause, Damage, Insurance | Expomis',
    description: 'After water damage: first steps, finding the cause, assessing the damage and the insurance claim. Expomis handles both leak detection and loss assessment.',
    eyebrow: 'Loss assessment',
    h1: 'Water damage: finding the cause and documenting the claim',
    intro: [
      'After water damage, two questions arise: where does the water come from, and who covers the damage? Expomis answers both, because we carry out both the [leak detection](/en/leak-detection-luxembourg) and the loss assessment.',
    ],
    sections: [
      {
        h2: 'First steps',
        ordered: true,
        list: [
          'Stop the water: turn off the main valve, or the one for the appliance concerned.',
          'Switch off the electricity in affected rooms if water is near sockets or appliances.',
          'Photograph the damage before moving or cleaning anything.',
          'Notify your insurer within the time limit set by your policy, and your neighbour or building manager (syndic) if other flats are affected.',
          'Keep damaged items until the assessment.',
        ],
      },
      {
        h2: 'Why the cause matters so much',
        body: [
          'Cover often depends on where the water comes from: a private pipe, a common part of the building, a neighbouring flat, water ingress through the roof, sewer backflow. Each case may fall under a different policy or a different party. Until the cause is precisely established, the claim can stall. Our guide [Water leak in Luxembourg: who pays?](/en/water-leak-who-pays) covers each situation.',
          'That is why we start by locating the leak, with non-destructive methods, before assessing the damage.',
        ],
      },
      {
        h2: 'What we do',
        list: [
          'locating the [leak](/en/leak-detection-luxembourg) or the [water ingress](/en/water-ingress);',
          'recording the damage: photos, moisture measurements, extent of the affected area;',
          'report within 72 hours, ready to send to your insurer;',
          'loss assessment, on behalf of an insurance company or as an independent adjuster on your side.',
        ],
      },
    ],
    faq: [
      {
        q: 'Who pays for leak detection?',
        a: 'In many cases, home insurance covers the cost of leak detection when the leak has caused water damage. Terms and limits vary: check your policy. Tenant, co-ownership, neighbour: see our guide [Water leak: who pays?](/en/water-leak-who-pays).',
      },
      {
        q: 'Do I have to wait for the adjuster before repairing?',
        a: 'Take urgent measures straight away to stop the leak and limit the damage: this is expected of you. Before permanent repairs, notify your insurer first and keep photos of the condition before the work.',
      },
    ],
    related: ['/en/water-leak-who-pays', '/en/leak-detection-luxembourg', '/en/shower-leak'],
  },
  {
    path: '/en/water-leak-who-pays',
    lang: 'en',
    translationOf: '/fuite-eau-qui-paie',
    parent: '/en/water-damage',
    guide: { datePublished: '2026-10-14' },
    nav: 'Water leak: who pays?',
    summary: 'Tenant, landlord, co-ownership, neighbour, public network: who covers the leak and the damage.',
    areaServed: AREAS.lu,
    title: 'Water Leak in Luxembourg: Who Pays? Full Guide | Expomis',
    description: 'Water leak in Luxembourg: who pays for the repair, the damage and leak detection? Tenant, landlord, co-ownership, neighbour, water meter, new build.',
    eyebrow: 'Guide — October 2026',
    h1: 'Water leak in Luxembourg: who pays?',
    intro: [
      'After a water leak, there are three costs: finding the leak, repairing what is leaking, and restoring what the water has damaged. They are not necessarily paid by the same person. Who pays depends mainly on one fact: exactly where the leak is.',
      "This guide sets out the general rules we come across as [leak detection specialists](/en/leak-detection-luxembourg) and loss adjusters in Luxembourg. It is not legal advice: your insurance policy, your lease and the co-ownership rules are what count. In case of dispute, contact your insurer, your legal protection insurance or a lawyer.",
    ],
    sections: [
      {
        h2: 'In short',
        list: [
          'House you own and live in: you pay for the repair; your home insurance generally covers water damage and often leak detection costs.',
          'Rented home: minor maintenance repairs are in principle the tenant’s responsibility, repairs to the pipes and installation the landlord’s, unless the tenant is at fault or failed to maintain.',
          'Co-ownership building (copropriété): a leak on a common part is the responsibility of the co-owners, represented by the syndic; a leak on a private part is the responsibility of the co-owner concerned.',
          'Leak coming from a neighbour: your damage is generally covered by your own insurer, which may then claim against the party responsible.',
          'Leak before the water meter: generally the public network (commune or water syndicate); after the meter, the owner.',
          'New or recent home: the leak may fall under the developer’s or contractors’ warranties.',
        ],
        after: [
          'In every case, it is the precise location of the leak that tells you which situation applies.',
        ],
      },
      {
        h2: 'Why it all depends on where the leak is',
        body: [
          "The same stain on a ceiling may come from a private pipe, a common riser in the building, the neighbour's shower, the roof or sewer backflow. Each of these sources points to a different responsible party and a different insurance policy. Until the source is established, everyone can pass the cost on to someone else, and the claim stays stuck.",
          'That is why a leak detection report that pinpoints the source, with photos and measurements, is often the first document insurers ask for.',
        ],
      },
      {
        h2: 'You own your house',
        body: [
          'Repairing the installation (pipe, fitting, heating, drain) is your responsibility: it is your property. Damage caused by the water (ceilings, walls, floors, furniture) is generally covered by your home insurance as water damage, according to your policy terms.',
          'Many policies also cover leak detection costs when a leak has caused water damage, often up to a limit. They less often cover repairing the pipe itself, and rarely the lost water.',
        ],
      },
      {
        h2: 'You are a tenant, or a landlord',
        body: [
          'The principle: the tenant handles routine maintenance and minor repairs (seals, flexible hoses, taps, clearing traps), the landlord the more significant repairs to the installation (concealed pipes, heating, drains). A leak on a concealed pipe is therefore generally the landlord’s responsibility, unless it results from the tenant’s fault or lack of maintenance.',
          "The tenant must notify the landlord promptly: letting a leak get worse without saying anything can make the tenant liable. Leases often require the tenant to hold tenant's liability insurance; it may cover the tenant's belongings and liability. The landlord's insurance generally covers the building.",
          'Who orders the leak detection? The tenant, the landlord or the syndic can. The simplest approach is to agree beforehand: the report, which pinpoints the source of the leak, is then used to share the costs.',
        ],
      },
      {
        h2: 'You live in a co-ownership building',
        body: [
          'The co-ownership rules distinguish private parts, belonging to each co-owner, from common parts, belonging to everyone. As a general rule, water and drainage risers, the roof and the façade are common parts, while pipes serving a single flat, after its stop valve, are private. But it is your building’s rules that set the boundary.',
          "A leak on a common part is paid for by the co-owners: notify the syndic, who involves the building's insurance. A leak on a private part is the responsibility of the co-owner concerned and their insurance.",
        ],
      },
      {
        h2: "The water is coming from the neighbour's",
        body: [
          'Report the claim to your own insurer: it is generally your insurer that compensates you under your policy, then claims, where appropriate, against the party responsible or their insurer. Also notify the neighbour and, in a co-ownership building, the syndic.',
          'To locate the leak, access will be needed to the flat it comes from. If the neighbour disputes it, the leak detection report establishes the facts.',
        ],
      },
      {
        h2: 'Before or after the meter',
        body: [
          "As a general rule, the connection up to the meter is part of the public network (commune or water syndicate), and everything after the meter is the owner's responsibility. The exact boundary and the rules on costs are set by your commune’s regulations: ask its water department.",
          'For a hidden leak after the meter, some communes grant a reduction on the water bill once the leak has been located and repaired. See our page on [high water consumption](/en/high-water-usage).',
        ],
      },
      {
        h2: 'New or recently renovated home',
        body: [
          'If the home is recent, the leak may fall under the warranties owed by the developer or the contractors who built or renovated it: two-year or ten-year warranty depending on the element concerned. Notify them in writing, and have the source of the leak documented before it is repaired, so the evidence is not lost.',
        ],
      },
      {
        h2: 'Water ingress and sewer backflow',
        body: [
          '[Water ingress](/en/water-ingress) through the roof, a terrace or the façade is generally the responsibility of the building owner or, in a co-ownership building, of the co-owners. Sewer backflow may fall to the public network or the private installation, depending on where it originates. Insurance policies treat these cases very differently, and some exclude them: check your terms.',
        ],
      },
      {
        h2: "If you don't agree with the settlement",
        body: [
          "If your insurer's adjuster's assessment seems too low, or the cause they identified seems wrong, you can appoint your own adjuster. This independent assessment is provided for in most policies.",
        ],
      },
    ],
    faq: [
      {
        q: 'Who pays for leak detection?',
        a: 'Whoever orders it generally pays upfront. It is often reimbursed by home insurance when the leak has caused water damage, up to a limit. In a rental or co-ownership, the costs may then be shared according to the source established by the report.',
      },
      {
        q: 'Will my insurance pay for the lost water?',
        a: 'Rarely. Policies mainly cover damage caused by water. However, some communes grant a reduction on the bill after a hidden leak has been located and repaired.',
      },
      {
        q: 'I am a tenant: do I have to pay for the leak repair?',
        a: 'Not if it concerns a concealed pipe or a significant part of the installation, unless you are at fault or failed to maintain it. Minor repairs (seals, flexible hoses, taps) are in principle yours. Your lease may specify how costs are split.',
      },
      {
        q: 'The neighbour upstairs refuses access to look for the leak. What can I do?',
        a: 'Notify your insurer and, in a co-ownership building, the syndic, who can contact them. Document the damage in your home with photos and measurements. If the deadlock continues, ask your insurer or a lawyer for advice.',
      },
      {
        q: 'Do I have to wait for the adjuster before repairing?',
        a: 'Take urgent measures straight away to stop the leak and limit the damage. Before permanent repairs, notify your insurer first and keep photos of the condition before the work.',
      },
    ],
    related: ['/en/water-damage', '/en/leak-detection-luxembourg', '/en/water-pipe-leak'],
  },  {
    path: '/en/leak-detection-cost',
    lang: 'en',
    translationOf: '/prix-recherche-fuite',
    parent: PARENT,
    nav: 'Leak detection cost',
    summary: 'A price agreed in advance for each job, with a flat rate possible for a simple leak search.',
    title: 'Leak detection cost in Luxembourg | Expomis',
    description: 'How much does leak detection cost in Luxembourg? A price agreed in advance for each job, a flat rate possible for a simple search, and insurance cover.',
    eyebrow: 'Prices',
    h1: 'Leak detection cost in Luxembourg',
    intro: [
      'At Expomis, leak detection is charged per job, at a price agreed in advance. You describe the situation, we explain how we would proceed and give you the price before we start. If the leak is found in a single visit, without taking anything apart, a flat rate applies when it works out cheaper for you.',
    ],
    sections: [
      {
        h2: 'A price agreed before we start',
        body: [
          'The price is agreed with you before any work begins, based on what you describe. You know what you will pay, and you can put it to your insurer or your building manager before you go ahead.',
          'We do not publish a single price, because no two leak searches are alike. A stain under a shower in a flat and a pressure loss on 150 m² of underfloor heating take neither the same time nor the same equipment. A published price would be too high for the first, or misleading for the second.',
        ],
      },
      {
        h2: 'A flat rate for a simple leak search',
        body: [
          'A leak search is simple when the leak is found in a single visit, without having to take anything apart to carry out the tests. In that case, a flat rate may apply.',
          'We work it out after the first visit: if the flat rate is better for you than the price of the job, the flat rate is what you pay.',
        ],
      },
      {
        h2: 'What affects the price',
        list: [
          'the type of system: a pressurised pipe, a [drain](/en/drain-leak) or [water ingress](/en/water-ingress) are not traced in the same way;',
          'the number of circuits to test: cold water, hot water, heating, underfloor heating loops;',
          'the size of the building: flat, house, block with common parts;',
          'the [methods](/en/leak-detection-methods) needed: [tracer gas](/en/leak-detection-methods/tracer-gas) or a [CCTV survey](/en/leak-detection-methods/cctv-drain-survey) take more preparation than a moisture reading;',
          'access to the installations: manholes, suspended ceilings, service ducts, manifold.',
        ],
      },
      {
        h2: 'What we need to give you a price',
        list: [
          'the type of property and the floor, and whether it is in co-ownership;',
          'what you have noticed, and since when: a stain, a meter that keeps turning, a boiler losing pressure, a smell;',
          'a few photos of the affected area, and of the meter if you suspect it;',
          'what has already been done: a plumber’s visit, opening up, a claim to your insurer.',
        ],
        after: ['You can send us these details through the [contact form](/en/contact) or give us a call.'],
      },
      {
        h2: 'Who pays for leak detection?',
        body: [
          'In Luxembourg, many home insurance policies cover the cost of leak detection when the leak has caused water damage, often up to a limit. Check your policy or ask your insurance agent. Our report is the document your insurer expects in order to handle the claim.',
          'Tenant, owner, co-ownership, neighbour: who pays depends on where the leak is. Our guide [Water leak in Luxembourg: who pays?](/en/water-leak-who-pays) covers each situation.',
        ],
      },
    ],
    faq: [
      {
        q: 'How much does leak detection cost in Luxembourg?',
        a: 'The price depends on the situation: the type of leak, the number of circuits to test and the methods needed. It is agreed with you before we start. If the leak is found in a single visit, without taking anything apart, we apply a flat rate when it works out cheaper for you. Describe the situation to us to get a price.',
      },
      {
        q: 'Why not publish a price?',
        a: 'Because it would only be right for some cases. We would rather give you, before we start, the price that matches your situation.',
      },
      {
        q: 'Does insurance pay for leak detection?',
        a: 'Often, when the leak has caused water damage, within the limits of your policy. Because the price is agreed in advance, you can check with your insurer before we start.',
      },
    ],
    related: ['/en/water-leak-who-pays', '/en/water-damage', '/en/leak-detection-luxembourg'],
  },
];
